import { useEffect, useRef, useState } from 'react';

import { useUiStore } from '../store/ui-store';
import {
  forgetExtensionSession,
  getExtensionSession,
  joinExtensionSession,
} from './use-extension-captures';

/**
 * The session an extension-captured page is filling, mirrored into the URL.
 *
 * Same contract as `useSession` on the worker side — arriving without
 * `?session=` starts a new session, arriving with one rejoins it — but
 * nothing is created here: the session is created by the first capture, which
 * arrives through `useExtensionCaptures`. All this does is decide which
 * session (and, for a new one, which project) the *next* capture joins, and
 * notice when one exists.
 */
export function useExtensionSession(
  target: string,
  sessionIdFromUrl: null | string,
  projectId: null | string,
  onResolved: (sessionId: string) => void,
): null | string {
  const [sessionId, setSessionId] = useState<null | string>(sessionIdFromUrl);

  // `landed` is only a tick: it says *some* capture landed, not which product.
  // The origin's own key is the answer, and it is already written by then.
  const landed = useUiStore((state) => state.landed);

  const claimedRef = useRef(false);
  useEffect(() => {
    if (claimedRef.current) return;
    claimedRef.current = true;

    // Arriving with a session means "continue in this session"; arriving
    // without one means a new session, which for the extension is forgetting
    // the last one. The first capture writes `?session=` into the URL below,
    // so a reload takes the first branch and does not quietly start a third.
    if (sessionIdFromUrl) joinExtensionSession(target, sessionIdFromUrl);
    else forgetExtensionSession(target, projectId);
  }, [projectId, sessionIdFromUrl, target]);

  useEffect(() => {
    const current = getExtensionSession(target);
    if (!current || current === sessionId) return;
    setSessionId(current);
    onResolved(current);
  }, [sessionId, landed, onResolved, target]);

  return sessionId;
}
